import { useMemo } from 'react';

import {
  ALINE_TOOL_NAMES,
  ASK_USER_CARD_TITLE_INPUT_KEY,
  getConnectorCardQueue,
  getPendingClientToolInput,
  getPendingToolApproval,
  WorkflowCollectIntakeFormInputSchema,
  WorkflowCollectLaunchInfoInputSchema,
} from '@aline/common';
import type { IntakeFormValues } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import { getIsPlainRecord } from 'src/utils/get-is-plain-record';

import { FALLBACK_ASK_USER_QUESTIONS, parseAskUserQuestions } from './AskUser';
import type { AskUserQuestion } from './AskUser';

export type PendingInteraction =
  | {
      kind: 'connectExternalAccount';
      messageId: string;
      queuePosition: { current: number; total: number };
      toolCallIds: readonly string[];
      toolkit: string;
    }
  | {
      kind: 'enableExternalTools';
      messageId: string;
      queuePosition: { current: number; total: number };
      requests: readonly { toolCallId: string; toolSlugs: readonly string[] }[];
      toolkit: string;
      unionSlugs: readonly string[];
    }
  | {
      kind: 'askUser';
      messageId: string;
      questions: AskUserQuestion[];
      /**
       * Heading the asking agent gave the card, naming what it is about to do.
       * Null for agents that did not set one, which render the default.
       */
      title: string | null;
      toolCallId: string;
    }
  | {
      kind: 'collectIntakeForm';
      messageId: string;
      suggestedValues: IntakeFormValues;
      templateWorkflowId: string;
      toolCallId: string;
    }
  | {
      kind: 'collectLaunchInfo';
      messageId: string;
      suggestedAlineFieldValues: Record<string, string>;
      suggestedIntakeValues: IntakeFormValues;
      suggestedRoleAssignments: Record<string, string>;
      templateWorkflowId: string;
      toolCallId: string;
    }
  | {
      approvalId: string;
      input: unknown;
      kind: 'approval';
      messageId: string;
      toolName: string;
    };

type AgentThreadMessages = Apollo.GetAgentThreadQuery['agentThread']['messages'];

/**
 * Surfaces the newest unresolved assistant interaction in a thread — a tool
 * approval or a client-input turn — so the chat panel can render its card above
 * the input, giving every "Aline needs you" moment the same interaction surface.
 * Reads from the shared agent-thread query cache (no extra network) and
 * re-evaluates as the user responds, so the card unmounts once resolved.
 */
export function usePendingInteraction(threadId: UUID | null): PendingInteraction | null {
  const { data } = Apollo.useGetAgentThreadQuery({
    skip: !threadId,
    variables: { id: threadId ?? '' },
  });
  const messages = data?.agentThread.messages;
  return useMemo(() => getPendingInteraction(messages), [messages]);
}

/** The server-written card heading, ignored unless it is a usable string. */
function getAskUserCardTitle(input: Record<string, unknown> | null): string | null {
  const title = input?.[ASK_USER_CARD_TITLE_INPUT_KEY];
  if (typeof title !== 'string') return null;
  return title.trim() || null;
}

function getPendingInteraction(
  messages: AgentThreadMessages | undefined
): PendingInteraction | null {
  if (!messages) return null;

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const uiParts = message.data?.uiParts;
    const connectorQueue = getConnectorCardQueue(uiParts);
    if (connectorQueue.length > 0) {
      const group = connectorQueue[0];
      const queuePosition = { current: 1, total: connectorQueue.length };
      if (group.kind === 'connect') {
        return {
          kind: 'connectExternalAccount',
          messageId: message.id,
          queuePosition,
          toolCallIds: group.toolCallIds,
          toolkit: group.toolkit,
        };
      }
      return {
        kind: 'enableExternalTools',
        messageId: message.id,
        queuePosition,
        requests: group.requests,
        toolkit: group.toolkit,
        unionSlugs: group.unionSlugs,
      };
    }

    const approval = getPendingToolApproval(uiParts);
    if (approval) {
      return {
        approvalId: approval.id,
        input: approval.part.input,
        kind: 'approval',
        messageId: message.id,
        toolName: approval.part.toolName ?? '',
      };
    }

    const pendingInput = getPendingClientToolInput(uiParts);
    if (pendingInput?.part.toolName === ALINE_TOOL_NAMES.workflowCollectIntakeForm) {
      const parsedInput = WorkflowCollectIntakeFormInputSchema.safeParse(pendingInput.part.input);
      if (!parsedInput.success) continue;
      return {
        kind: 'collectIntakeForm',
        messageId: message.id,
        suggestedValues: parsedInput.data.suggestedValues ?? {},
        templateWorkflowId: parsedInput.data.templateWorkflowId,
        toolCallId: pendingInput.toolCallId,
      };
    }
    if (pendingInput?.part.toolName === ALINE_TOOL_NAMES.workflowCollectLaunchInfo) {
      const parsedInput = WorkflowCollectLaunchInfoInputSchema.safeParse(pendingInput.part.input);
      if (!parsedInput.success) continue;

      return {
        kind: 'collectLaunchInfo',
        messageId: message.id,
        suggestedAlineFieldValues: parsedInput.data.suggestedAlineFieldValues ?? {},
        suggestedIntakeValues: parsedInput.data.suggestedIntakeValues ?? {},
        suggestedRoleAssignments: parsedInput.data.suggestedRoleAssignments ?? {},
        templateWorkflowId: parsedInput.data.templateWorkflowId,
        toolCallId: pendingInput.toolCallId,
      };
    }

    if (pendingInput?.part.toolName === ALINE_TOOL_NAMES.askUser) {
      const input = pendingInput.part.input;
      const record = getIsPlainRecord(input) ? input : null;
      const questions = record ? parseAskUserQuestions(record.questions) : null;
      /*
       * A pending askUser part means the turn is suspended on this question, so
       * always surface a card. When the payload can't be parsed, fall back to a
       * generic free-text prompt rather than dropping the card and stranding the
       * turn with no way to answer it.
       */
      return {
        kind: 'askUser',
        messageId: message.id,
        questions: questions ?? FALLBACK_ASK_USER_QUESTIONS,
        title: getAskUserCardTitle(record),
        toolCallId: pendingInput.toolCallId,
      };
    }
  }

  return null;
}
